/**
 * Read-only typography for a rendered RichEditor body.
 *
 * These are Tailwind arbitrary variants applied to the element *wrapping* the
 * editor, deliberately not app CSS: the `.rich-editor` rules are duplicated in
 * dashboard/src/index.css and files-viewer/src/index.css, neither of which this
 * package owns. A wrapper variant compiles to `.wrapper .ProseMirror …`, one
 * class more specific than the app's `.rich-editor .ProseMirror …`, so it wins
 * without `!important`; the `!` modifier is used only where an app rule sets
 * the same property on the same-specificity selector (the editor's own padding,
 * the Dx-report image cap, the first/last block margins).
 *
 * Tailwind's scanner reads these as literal tokens in this file, so every
 * consuming app generates them.
 */
export const RICH_BODY_TYPOGRAPHY_CLASS = [
    // The editor's own gutter has no place in a rendered body - it indents the
    // text relative to everything above it.
    '[&_.ProseMirror]:!px-0',
    '[&_.ProseMirror]:!py-0',
    // 14px/gray-700 to match the surrounding UI, rather than the editor's 16px.
    '[&_.ProseMirror]:text-sm',
    '[&_.ProseMirror]:leading-relaxed',
    '[&_.ProseMirror]:text-gray-700',
    // Tailwind preflight zeroes paragraph margins and nothing restores them, so
    // consecutive paragraphs read as one block of text.
    '[&_.ProseMirror_p]:my-0',
    '[&_.ProseMirror_p+p]:mt-3',
    // Block margins must not push the body away from its container edges.
    '[&_.ProseMirror>*:first-child]:!mt-0',
    '[&_.ProseMirror>*:last-child]:!mb-0',
    '[&_.ProseMirror_blockquote]:my-4',
    '[&_.ProseMirror_blockquote]:border-l-4',
    '[&_.ProseMirror_blockquote]:border-gray-200',
    '[&_.ProseMirror_blockquote]:pl-4',
    '[&_.ProseMirror_blockquote]:italic',
    '[&_.ProseMirror_blockquote]:text-gray-600',
    '[&_.ProseMirror_code]:rounded',
    '[&_.ProseMirror_code]:bg-gray-100',
    '[&_.ProseMirror_code]:px-1',
    '[&_.ProseMirror_code]:py-0.5',
    '[&_.ProseMirror_code]:font-mono',
    '[&_.ProseMirror_code]:text-[0.9em]',
    '[&_.ProseMirror_code]:text-gray-800',
    '[&_.ProseMirror_pre]:my-4',
    '[&_.ProseMirror_pre]:overflow-x-auto',
    '[&_.ProseMirror_pre]:rounded-lg',
    '[&_.ProseMirror_pre]:bg-gray-900',
    '[&_.ProseMirror_pre]:p-3',
    '[&_.ProseMirror_pre]:text-xs',
    '[&_.ProseMirror_pre]:leading-relaxed',
    '[&_.ProseMirror_pre]:text-gray-100',
    '[&_.ProseMirror_pre_code]:!bg-transparent',
    '[&_.ProseMirror_pre_code]:!p-0',
    '[&_.ProseMirror_pre_code]:!text-inherit',
    // Standalone images (the legacy `image` node, a direct child of the doc).
    // The app CSS caps every `.rich-editor .ProseMirror img` at 220px tall and
    // floats it left - written for the Dx report editor, wrong for a body whose
    // whole point can be a photo. Scoped to `>` so grid cells, whose images are
    // deliberately letterboxed to the cell, keep their own rules.
    '[&_.ProseMirror>img]:!block',
    '[&_.ProseMirror>img]:!mx-auto',
    '[&_.ProseMirror>img]:!my-4',
    '[&_.ProseMirror>img]:!max-h-none',
    '[&_.ProseMirror>img]:!max-w-full',
    '[&_.ProseMirror>img]:h-auto',
    '[&_.ProseMirror>img]:rounded-lg'
].join(' ');

/** Applied by RichEditor itself in read-only mode: images open a lightbox. */
export const RICH_BODY_ZOOMABLE_IMAGE_CLASS = '[&_.ProseMirror_img]:cursor-zoom-in';
